import React, { useState, useEffect, useMemo } from 'react';
import { studentService, classService } from '../../services/index.js';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { 
  FileText, Search, Filter, Download, Printer, User, Calendar, CheckCircle2, Clock, AlertTriangle, X, Eye, RefreshCw
} from 'lucide-react';

const AdmissionReports = ({ orgData, students: propStudents = [], classes: propClasses = [], showNotification }) => {
  const orgId = orgData?.orgId || orgData?.id || 'SCH-02';

  // Core Data States
  const [students, setStudents] = useState(propStudents || []);
  const [classes, setClasses] = useState(propClasses || []);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Selected Student for View Modal
  const [selectedStudentForView, setSelectedStudentForView] = useState(null);

  // Filter States
  const [academicYear, setAcademicYear] = useState('ALL');
  const [filterClass, setFilterClass] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Sync props
  useEffect(() => {
    if (Array.isArray(propStudents) && propStudents.length > 0) setStudents(propStudents);
    if (Array.isArray(propClasses) && propClasses.length > 0) setClasses(propClasses);
  }, [propStudents, propClasses]);

  // Load students if prop is empty
  useEffect(() => {
    loadStudents();
  }, [orgId]);

  const loadStudents = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      if (!propStudents || propStudents.length === 0) {
        const res = await studentService.getStudents();
        const list = Array.isArray(res) ? res : (res?.data || res?.students || []);
        setStudents(list);
      }
      if (!propClasses || propClasses.length === 0) {
        const resCls = await classService.getAllClasses();
        const listCls = Array.isArray(resCls) ? resCls : (resCls?.data || resCls?.classes || []);
        setClasses(listCls);
      }
    } catch (err) {
      console.error('Error loading admission reports data:', err);
      setLoadError('Unable to load admission records from server');
    } finally {
      setLoading(false);
    }
  };

  // Helper date formatting
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'Invalid Date') return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  // Extract unique classes and sections
  const availableClasses = useMemo(() => {
    const setCls = new Set();
    students.forEach(s => {
      const c = s.className || s.class;
      if (c) setCls.add(String(c));
    });
    classes.forEach(c => {
      if (c.className || c.name) setCls.add(String(c.className || c.name));
    });
    return Array.from(setCls).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [students, classes]);

  const availableSections = useMemo(() => {
    const setSec = new Set();
    students.forEach(s => {
      if (s.section) setSec.add(String(s.section));
    });
    return Array.from(setSec).sort();
  }, [students]);

  // Filtered Admissions List
  const filteredStudents = useMemo(() => {
    return students.filter(s => {
      // Academic Year Filter
      const sYear = s.admissionYear;
      const matchYear = !academicYear || academicYear === 'ALL' || !sYear || String(sYear) === String(academicYear);

      // Class & Section Filter
      const sClass = s.className || s.class || '';
      const sSec = s.section || '';
      const matchClass = !filterClass || String(sClass).toLowerCase() === String(filterClass).toLowerCase();
      const matchSection = !filterSection || String(sSec).toLowerCase() === String(filterSection).toLowerCase();

      // Status Filter
      const statusStr = (s.registrationStatus || s.status || 'Active').toUpperCase();
      let matchStatus = true;
      if (filterStatus === 'ACTIVE') {
        matchStatus = statusStr === 'APPROVED' || statusStr === 'ACTIVE';
      } else if (filterStatus === 'PENDING') {
        matchStatus = statusStr === 'PENDING';
      } else if (filterStatus === 'CANCELLED') {
        matchStatus = statusStr === 'REJECTED' || statusStr === 'WITHDRAWN' || statusStr === 'CANCELLED' || statusStr === 'INACTIVE';
      }

      // Date Range Filter
      let matchDate = true;
      const sDateStr = s.admissionDate || s.createdAt;
      if (sDateStr && (fromDate || toDate)) {
        const sDate = new Date(sDateStr).getTime();
        if (fromDate && !isNaN(new Date(fromDate).getTime())) {
          matchDate = matchDate && sDate >= new Date(fromDate).getTime();
        }
        if (toDate && !isNaN(new Date(toDate).getTime())) {
          matchDate = matchDate && sDate <= new Date(toDate).getTime() + 86400000;
        }
      }

      // Search Filter
      const q = searchTerm.toLowerCase().trim();
      const matchSearch = !q || 
        (s.name || s.studentName || '').toLowerCase().includes(q) ||
        (s.studentId || '').toLowerCase().includes(q) ||
        (s.admissionNo || '').toLowerCase().includes(q) ||
        (s.rollNo || '').toLowerCase().includes(q);

      return matchYear && matchClass && matchSection && matchStatus && matchDate && matchSearch;
    });
  }, [students, academicYear, filterClass, filterSection, filterStatus, fromDate, toDate, searchTerm]);

  // KPI Calculations
  const kpis = useMemo(() => {
    const total = filteredStudents.length;

    const pending = filteredStudents.filter(s => {
      const st = (s.registrationStatus || '').toUpperCase();
      return st === 'PENDING';
    }).length;

    const cancelled = filteredStudents.filter(s => {
      const st = (s.registrationStatus || s.status || '').toUpperCase();
      return st === 'REJECTED' || st === 'WITHDRAWN' || st === 'CANCELLED' || st === 'INACTIVE';
    }).length;

    const activeCount = total - pending - cancelled;

    return { total, activeCount, pending, cancelled };
  }, [filteredStudents]);

  // Export to Excel / CSV
  const handleExportExcel = () => {
    if (filteredStudents.length === 0) {
      if (showNotification) showNotification('warning', 'No Data', 'No admission records available to export');
      return;
    }

    const exportData = filteredStudents.map(s => ({
      'Admission No': s.admissionNo || s.studentId || '—',
      'Student Name': s.name || s.studentName || '—',
      'Admission Date': formatDate(s.admissionDate || s.createdAt),
      'Class': s.className || s.class || '—',
      'Section': s.section || '—',
      'Academic Year': s.admissionYear || academicYear || '2026-27',
      'Gender': s.gender || '—',
      'Father Name': s.fatherName || s.parentName || '—',
      'Phone Number': s.phone || s.parentPhone || '—',
      'Status': s.registrationStatus || s.status || 'Active'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Admission_Register');
    XLSX.writeFile(workbook, `Admission_Register_${academicYear}_${new Date().toISOString().split('T')[0]}.xlsx`);
    if (showNotification) showNotification('success', 'Export Complete', 'Admission register Excel file downloaded successfully');
  };

  // Export to PDF using native jsPDF
  const handleExportPDF = () => {
    if (filteredStudents.length === 0) {
      if (showNotification) showNotification('warning', 'No Data', 'No admission records available to export');
      return;
    }

    try {
      const doc = new jsPDF();
      const schoolName = orgData?.orgName || orgData?.institutionName || orgData?.name || 'Institution';
      
      // Header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(37, 99, 235); // Blue
      doc.text(schoolName, 14, 15);

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(`Official Admission Register — Academic Year ${academicYear}`, 14, 23);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated Date: ${new Date().toLocaleDateString('en-IN')} | Total Records: ${filteredStudents.length}`, 14, 29);

      // Line Separator
      doc.setDrawColor(226, 232, 240);
      doc.line(14, 32, 196, 32);

      // Table Headers
      let y = 40;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text("Adm No", 14, y);
      doc.text("Student Name", 50, y);
      doc.text("Class", 110, y);
      doc.text("Sec", 135, y);
      doc.text("Adm Date", 150, y);
      doc.text("Status", 180, y);

      y += 4;
      doc.setDrawColor(203, 213, 225);
      doc.line(14, y, 196, y);
      y += 6;

      // Table Rows
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(51, 65, 85);

      filteredStudents.forEach((s) => {
        if (y > 280) {
          doc.addPage();
          y = 20;
        }

        const admNo = s.admissionNo || s.studentId || `ADM-${s.id}`;
        const name = (s.name || s.studentName || 'Student').substring(0, 25);
        const cls = `Class ${s.className || s.class || '—'}`;
        const sec = s.section || 'A';
        const admDate = formatDate(s.admissionDate || s.createdAt);
        const status = (s.registrationStatus || s.status || 'ACTIVE').toUpperCase();

        doc.text(String(admNo), 14, y);
        doc.text(String(name), 50, y);
        doc.text(String(cls), 110, y);
        doc.text(String(sec), 135, y);
        doc.text(String(admDate), 150, y);
        doc.text(String(status), 180, y);

        y += 7;
      });

      doc.save(`Admission_Register_${academicYear}.pdf`);
      if (showNotification) showNotification('success', 'Export Complete', 'Admission register PDF downloaded successfully');
    } catch (e) {
      console.error('PDF export error:', e);
      if (showNotification) showNotification('error', 'Export Failed', 'Failed to generate PDF report');
    }
  };

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', padding: '20px', fontFamily: '"Inter", sans-serif' }}>
      
      {/* ── Page Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '20px', background: '#ffffff', padding: '20px 24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '22px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
            <FileText size={24} color="#2563eb" /> Admission Reports & Register
          </h1>
          <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 34px' }}>
            Track student admissions, enrollment trends, and admission status for {orgData?.orgName || orgData?.institutionName || orgData?.name || 'your institution'}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={handleExportExcel}
            style={{ padding: '9px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Download size={15} /> Export Excel
          </button>

          <button 
            onClick={handleExportPDF}
            style={{ padding: '9px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Printer size={15} /> Export PDF
          </button>
        </div>
      </div>

      {/* ── API Load Error Warning ── */}
      {loadError && (
        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '500' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <AlertTriangle size={18} color="#dc2626" />
            <span>{loadError}</span>
          </div>
          <button onClick={loadStudents} style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
            Retry Loading
          </button>
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', background: '#ffffff', padding: '14px 20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
        
        {/* Search */}
        <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
          <div style={{ position: 'relative' }}>
            <Search size={15} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
            <input 
              type="text" 
              placeholder="Search student or adm no..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12.5px', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {/* Academic Year */}
        <div style={{ width: '135px' }}>
          <select 
            value={academicYear} 
            onChange={e => setAcademicYear(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12.5px', background: '#fff', outline: 'none' }}
          >
            <option value="ALL">All AY Years</option>
            <option value="2026-27">AY 2026-27</option>
            <option value="2025-26">AY 2025-26</option>
          </select>
        </div>

        {/* Class */}
        <div style={{ width: '135px' }}>
          <select 
            value={filterClass} 
            onChange={e => setFilterClass(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12.5px', background: '#fff', outline: 'none' }}
          >
            <option value="">All Classes</option>
            {availableClasses.map(cls => (
              <option key={cls} value={cls}>Class {cls}</option>
            ))}
          </select>
        </div>

        {/* Section */}
        <div style={{ width: '120px' }}>
          <select 
            value={filterSection} 
            onChange={e => setFilterSection(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12.5px', background: '#fff', outline: 'none' }}
          >
            <option value="">All Sections</option>
            {availableSections.map(sec => (
              <option key={sec} value={sec}>Sec {sec}</option>
            ))}
          </select>
        </div>

        {/* Status */}
        <div style={{ width: '140px' }}>
          <select 
            value={filterStatus} 
            onChange={e => setFilterStatus(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12.5px', background: '#fff', outline: 'none' }}
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active / Approved</option>
            <option value="PENDING">Pending Approval</option>
            <option value="CANCELLED">Cancelled / Withdrawn</option>
          </select>
        </div>

        {/* Date Range */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input 
            type="date" 
            value={fromDate} 
            onChange={e => setFromDate(e.target.value)}
            title="From Date"
            style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', outline: 'none' }}
          />
          <span style={{ fontSize: '12px', color: '#94a3b8' }}>to</span>
          <input 
            type="date" 
            value={toDate} 
            onChange={e => setToDate(e.target.value)}
            title="To Date"
            style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', outline: 'none' }}
          />
        </div>

        {(filterClass || filterSection || filterStatus || searchTerm || fromDate || toDate) && (
          <button 
            onClick={() => { setFilterClass(''); setFilterSection(''); setFilterStatus(''); setSearchTerm(''); setFromDate(''); setToDate(''); }}
            style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '12px', fontWeight: '600', cursor: 'pointer', marginLeft: 'auto' }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* ── 4 KPI Summary Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        
        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={24} color="#2563eb" />
            </div>
            <div>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Total Admissions</span>
              <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '2px 0 0 0' }}>{kpis.total}</h3>
            </div>
          </div>
        </div>

        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '10px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle2 size={24} color="#16a34a" />
            </div>
            <div>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Active Enrolled</span>
              <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '2px 0 0 0' }}>{kpis.activeCount}</h3>
            </div>
          </div>
        </div>

        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '10px', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock size={24} color="#d97706" />
            </div>
            <div>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Pending Approval</span>
              <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '2px 0 0 0' }}>{kpis.pending}</h3>
            </div>
          </div>
        </div>

        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '10px', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertTriangle size={24} color="#dc2626" />
            </div>
            <div>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Cancelled / Withdrawn</span>
              <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '2px 0 0 0' }}>{kpis.cancelled}</h3>
            </div>
          </div>
        </div>

      </div>

      {/* ── Admission Register Table ── */}
      <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Official Admission Register Ledger</h3>
          <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>Showing {filteredStudents.length} Records</span>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            <RefreshCw size={24} className="spin" style={{ marginBottom: '8px' }} />
            <p>Loading admission records...</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div style={{ padding: '50px', textAlign: 'center', color: '#64748b' }}>
            <FileText size={40} color="#94a3b8" style={{ marginBottom: '12px' }} />
            <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', color: '#334155' }}>No admission records found for the selected filters</h4>
            <p style={{ margin: 0, fontSize: '13px' }}>Try adjusting your search criteria, class filter, or date range.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                  <th style={{ padding: '12px' }}>Admission No</th>
                  <th style={{ padding: '12px' }}>Student Name</th>
                  <th style={{ padding: '12px' }}>Admission Date</th>
                  <th style={{ padding: '12px' }}>Class</th>
                  <th style={{ padding: '12px' }}>Section</th>
                  <th style={{ padding: '12px' }}>Academic Year</th>
                  <th style={{ padding: '12px' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((s, idx) => {
                  const statusStr = (s.registrationStatus || s.status || 'Active').toUpperCase();
                  const isApproved = statusStr === 'APPROVED' || statusStr === 'ACTIVE';
                  const isPending = statusStr === 'PENDING';
                  return (
                    <tr key={s.id || s.studentId || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px', fontWeight: '700', color: '#2563eb' }}>{s.admissionNo || s.studentId || `ADM-${s.id}`}</td>
                      <td style={{ padding: '12px', fontWeight: '700', color: '#0f172a' }}>{s.name || s.studentName || 'Student'}</td>
                      <td style={{ padding: '12px', color: '#475569' }}>{formatDate(s.admissionDate || s.createdAt)}</td>
                      <td style={{ padding: '12px', color: '#475569', fontWeight: '600' }}>Class {s.className || s.class || '—'}</td>
                      <td style={{ padding: '12px', color: '#475569' }}>{s.section || '—'}</td>
                      <td style={{ padding: '12px', color: '#475569' }}>{s.admissionYear || academicYear || '2026-27'}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ 
                          fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '12px',
                          background: isApproved ? '#dcfce7' : isPending ? '#fef3c7' : '#fee2e2',
                          color: isApproved ? '#166534' : isPending ? '#92400e' : '#991b1b'
                        }}>
                          {isApproved ? 'ACTIVE' : isPending ? 'PENDING' : 'CANCELLED'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        <button 
                          onClick={() => setSelectedStudentForView(s)}
                          style={{ background: '#eff6ff', border: '1px solid #bfdbfe', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        >
                          <Eye size={13} /> View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── View Admission Details Modal ── */}
      {selectedStudentForView && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', width: '90%', maxWidth: '600px', padding: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                📄 Admission Details Profile
              </h3>
              <button onClick={() => setSelectedStudentForView(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '13px', marginBottom: '20px' }}>
              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>STUDENT NAME</span>
                <strong style={{ color: '#0f172a', fontSize: '15px' }}>{selectedStudentForView.name || selectedStudentForView.studentName}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>ADMISSION / STUDENT ID</span>
                <strong style={{ color: '#2563eb', fontSize: '15px' }}>{selectedStudentForView.admissionNo || selectedStudentForView.studentId}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>CLASS & SECTION</span>
                <strong style={{ color: '#0f172a' }}>Class {selectedStudentForView.className || selectedStudentForView.class || '—'} ({selectedStudentForView.section || 'Sec A'})</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>ACADEMIC YEAR</span>
                <strong style={{ color: '#0f172a' }}>{selectedStudentForView.admissionYear || academicYear}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>ADMISSION DATE</span>
                <strong>{formatDate(selectedStudentForView.admissionDate || selectedStudentForView.createdAt)}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>GENDER / DOB</span>
                <strong>{selectedStudentForView.gender || '—'} • {selectedStudentForView.dob || '—'}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>FATHER NAME</span>
                <strong>{selectedStudentForView.fatherName || selectedStudentForView.parentName || '—'}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>CONTACT PHONE</span>
                <strong style={{ color: '#059669' }}>📞 {selectedStudentForView.phone || selectedStudentForView.parentPhone || '—'}</strong>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setSelectedStudentForView(null)} style={{ padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdmissionReports;
